import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  useCancelScheduledMessage,
  useScheduledMessages,
  useSendMessageImmediately,
} from "api/hooks/useScheduledMessages";
import { ScheduledEmailStatus } from "api/scheduledemails/scheduledemailsApi";
import { ScheduledMessageApi } from "api/types/ScheduledMessage";
import { routes } from "consts";
import { PaziaButton } from "components/atoms";
import {
  TanTable,
  TanTableColumn,
  useFilters,
  usePagination,
  useSorting,
} from "components/molecules";
import { formatDate } from "utils";
import { EmailPageHeader } from "../components/EmailPageHeader";

const FILTER_NAME = "scheduled-messages-filter";

const STATUS_LABELS = {
  [ScheduledEmailStatus.Draft]: "Szkic",
  [ScheduledEmailStatus.Scheduled]: "Zaplanowane",
  [ScheduledEmailStatus.Sending]: "Wysyłanie",
  [ScheduledEmailStatus.Sent]: "Wysłane",
  [ScheduledEmailStatus.Failed]: "Niepowodzenie",
  [ScheduledEmailStatus.Cancelled]: "Anulowane",
};

const STATUS_COLORS = {
  [ScheduledEmailStatus.Draft]: "text-gray-600",
  [ScheduledEmailStatus.Scheduled]: "text-blue-600",
  [ScheduledEmailStatus.Sending]: "text-yellow-600",
  [ScheduledEmailStatus.Sent]: "text-green-600",
  [ScheduledEmailStatus.Failed]: "text-red-600",
  [ScheduledEmailStatus.Cancelled]: "text-gray-500",
};

export const ScheduledMessagesPage = () => {
  const { searchPhrase, filter, updateAllFilters } = useFilters(FILTER_NAME);
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting("createdDate", "DESC");

  const navigate = useNavigate();

  const { isFetching, isLoading, scheduledMessages, total, invalidate } = useScheduledMessages(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  const sendImmediatelyMutation = useSendMessageImmediately();
  const cancelMutation = useCancelScheduledMessage();

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const handleView = (message: ScheduledMessageApi) => {
    navigate(`${routes.EMAILS}${routes.SCHEDULED_MESSAGES}${message.id}`);
  };

  const cols: TanTableColumn<ScheduledMessageApi>[] = [
    {
      id: "title",
      accessorKey: "title",
      header: "Tytuł",
      size: 200,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      size: 120,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const status = info.getValue<ScheduledEmailStatus>();
        return <span className={`font-medium ${STATUS_COLORS[status]}`}>{STATUS_LABELS[status]}</span>;
      },
    },
    {
      id: "plannedDeliveryTime",
      accessorKey: "plannedDeliveryTime",
      header: "Planowana wysyłka",
      size: 150,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const date = info.getValue<Date>();
        const now = new Date();
        const isPast = date < now;
        const status = info.row.original.status;

        return (
          <span className={isPast && status === ScheduledEmailStatus.Scheduled ? "text-orange-500" : ""}>
            {formatDate(date, true, true)}
          </span>
        );
      },
    },
    {
      id: "recipients",
      accessorKey: "totalRecipients",
      header: "Odbiorcy",
      size: 100,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const total = info.getValue<number>();
        const sent = info.row.original.sentCount;
        const failed = info.row.original.failedCount;

        if (total === 0) return "0";

        return (
          <div className="text-sm">
            <div className="flex items-center gap-1">
              <span>{total} łącznie</span>
              <span className="text-xs text-gray-500">📱</span>
            </div>
            {sent > 0 && <div className="text-green-600">{sent} wysłane</div>}
            {failed > 0 && <div className="text-red-600">{failed} błędy</div>}
          </div>
        );
      },
    },
    {
      id: "content",
      accessorKey: "content",
      header: "Treść",
      size: 200,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => {
        const content = info.getValue<string>();

        if (!content) return <span className="italic text-gray-400">Brak treści</span>;

        const truncated = content.length > 50 ? content.substring(0, 50) + "..." : content;
        return (
          <div className="text-sm">
            <div className="text-gray-900">{truncated}</div>
            <div className="text-xs text-gray-500">{content.length} znaków</div>
          </div>
        );
      },
    },
    {
      id: "sender",
      accessorKey: "sender",
      header: "Nadawca",
      size: 120,
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info) => info.getValue<string>() || "SzkoleniaGW",
    },
    {
      id: "createdByUser",
      accessorKey: "createdByUser.email",
      header: "Utworzył",
      size: 150,
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: "createdDate",
      accessorKey: "createdDate",
      header: "Utworzono",
      size: 130,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => formatDate(info.getValue<Date>(), true, true),
    },
  ];

  const columns = React.useMemo<TanTableColumn<ScheduledMessageApi>[]>(
    () => cols,
    [sendImmediatelyMutation.isPending, cancelMutation.isPending],
  );

  return (
    <div>
      <div className="flex justify-between">
        <EmailPageHeader>Wiadomości SMS</EmailPageHeader>
        <PaziaButton onClick={() => navigate(routes.EMAILS + routes.SCHEDULED_MESSAGES_CREATE)} className="mb-4">
          Zaplanuj nowy SMS
        </PaziaButton>
      </div>

      <TanTable
        data={scheduledMessages}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        onRowClick={(message) => handleView(message)}
      />
    </div>
  );
};
